import type { Replacement } from '../engine/types';

export type ApplyResult =
  | { ok: true; text: string; from: number; to: number; insert: string }
  | { ok: false; reason: 'line-changed' | 'out-of-range'; currentLine?: string };

/**
 * Applies a fix's source-line replacement to `text`.
 * The edit happens only if line `line_no` (1-based) still reads exactly `old_text`
 * (trailing whitespace and a trailing CR are ignored): no guessing, no silent fix.
 * `from`/`to` are the character offsets of the replaced line content (without its newline).
 */
export function applyReplacement(text: string, replacement: Replacement): ApplyResult {
  const { line_no, old_text, new_text } = replacement;
  if (!Number.isInteger(line_no) || line_no < 1) return { ok: false, reason: 'out-of-range' };
  let from = 0;
  for (let i = 1; i < line_no; i++) {
    const nl = text.indexOf('\n', from);
    if (nl === -1) return { ok: false, reason: 'out-of-range' };
    from = nl + 1;
  }
  const nl = text.indexOf('\n', from);
  let to = nl === -1 ? text.length : nl;
  if (to > from && text[to - 1] === '\r') to -= 1;
  const current = text.slice(from, to);
  if (current.trimEnd() !== old_text.replace(/\r$/, '').trimEnd()) {
    return { ok: false, reason: 'line-changed', currentLine: current };
  }
  return { ok: true, text: text.slice(0, from) + new_text + text.slice(to), from, to, insert: new_text };
}
